/**
 * Centralized logging utility for the Bloom application
 * 
 * This module provides a consistent logging interface across the application,
 * with support for different log levels, structured logging, and integration
 * with monitoring services.
 */

// Log levels
export const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4
};

// Current log level (can be configured via environment)
const CURRENT_LOG_LEVEL = import.meta.env.VITE_LOG_LEVEL 
  ? parseInt(import.meta.env.VITE_LOG_LEVEL) 
  : import.meta.env.DEV ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO;

// Enable/disable console logging
const ENABLE_CONSOLE = import.meta.env.VITE_ENABLE_CONSOLE_LOGS !== 'false';

// Remote logging configuration
const REMOTE_LOGGING_ENABLED = import.meta.env.VITE_REMOTE_LOGGING_ENABLED === 'true';
const REMOTE_LOGGING_ENDPOINT = import.meta.env.VITE_REMOTE_LOGGING_ENDPOINT;

/**
 * Format log message with timestamp and context
 */
function formatLogMessage(level, message, context = {}) {
  const timestamp = new Date().toISOString();
  const levelName = Object.keys(LOG_LEVELS)[level];
  
  return {
    timestamp,
    level: levelName,
    message,
    context,
    url: window.location.href,
    userAgent: navigator.userAgent,
    userId: getCurrentUserId(),
    sessionId: getSessionId()
  };
}

/**
 * Get current user ID (if available)
 */
function getCurrentUserId() {
  try {
    // Try to get from Firebase Auth
    const auth = window.firebase?.auth?.();
    return auth?.currentUser?.uid || null;
  } catch (error) {
    return null;
  }
}

/**
 * Get or create session ID
 */
function getSessionId() {
  let sessionId = sessionStorage.getItem('bloom_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('bloom_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Send log to remote service
 */
async function sendToRemoteService(logData) {
  if (!REMOTE_LOGGING_ENABLED || !REMOTE_LOGGING_ENDPOINT) {
    return;
  }

  try {
    await fetch(REMOTE_LOGGING_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logData)
    });
  } catch (error) {
    // Silently fail remote logging to avoid infinite loops
    console.warn('Failed to send log to remote service:', error);
  }
}

/**
 * Core logging function
 */
function log(level, message, context = {}) {
  // Check if this log level should be processed
  if (level < CURRENT_LOG_LEVEL) {
    return;
  }

  const logData = formatLogMessage(level, message, context);

  // Console logging
  if (ENABLE_CONSOLE) {
    const consoleMethod = getConsoleMethod(level);
    const prefix = `[${logData.timestamp}] [${logData.level}]`;
    
    if (typeof message === 'string') {
      consoleMethod(`${prefix} ${message}`, context);
    } else {
      consoleMethod(prefix, message, context);
    }
  }

  // Remote logging (async, non-blocking)
  if (REMOTE_LOGGING_ENABLED) {
    sendToRemoteService(logData).catch(() => {
      // Silently handle remote logging failures
    });
  }

  // Integration with error monitoring services
  if (level >= LOG_LEVELS.ERROR) {
    // Sentry integration (if available)
    if (window.Sentry) {
      if (message instanceof Error) {
        window.Sentry.captureException(message, { contexts: { custom: context } });
      } else {
        window.Sentry.captureMessage(message, 'error', { contexts: { custom: context } });
      }
    }

    // LogRocket integration (if available)
    if (window.LogRocket) {
      window.LogRocket.captureException(message instanceof Error ? message : new Error(message));
    }
  }
}

/**
 * Get appropriate console method for log level
 */
function getConsoleMethod(level) {
  switch (level) {
    case LOG_LEVELS.DEBUG:
      return console.debug;
    case LOG_LEVELS.INFO:
      return console.info;
    case LOG_LEVELS.WARN:
      return console.warn;
    case LOG_LEVELS.ERROR:
    case LOG_LEVELS.FATAL:
      return console.error;
    default:
      return console.log;
  }
}

/**
 * Public logging interface
 */
export const logger = {
  /**
   * Debug level logging - for detailed diagnostic information
   */
  debug: (message, context) => log(LOG_LEVELS.DEBUG, message, context),

  /**
   * Info level logging - for general information
   */
  info: (message, context) => log(LOG_LEVELS.INFO, message, context),

  /**
   * Warning level logging - for potentially harmful situations
   */
  warn: (message, context) => log(LOG_LEVELS.WARN, message, context),

  /**
   * Error level logging - for error events that might still allow the application to continue
   */
  error: (message, context) => log(LOG_LEVELS.ERROR, message, context),

  /**
   * Fatal level logging - for very severe error events that might cause the application to abort
   */
  fatal: (message, context) => log(LOG_LEVELS.FATAL, message, context),

  /**
   * Log user actions for analytics
   */
  userAction: (action, details = {}) => {
    log(LOG_LEVELS.INFO, `User Action: ${action}`, {
      type: 'user_action',
      action,
      ...details
    });
  },

  /**
   * Log performance metrics
   */
  performance: (metric, value, unit = 'ms') => {
    log(LOG_LEVELS.INFO, `Performance: ${metric}`, {
      type: 'performance',
      metric,
      value,
      unit
    });
  },

  /**
   * Log API calls
   */
  apiCall: (method, url, status, duration, error = null) => {
    const level = error ? LOG_LEVELS.ERROR : LOG_LEVELS.INFO;
    log(level, `API Call: ${method} ${url}`, {
      type: 'api_call',
      method,
      url,
      status,
      duration,
      error: error ? error.message : null
    });
  },

  /**
   * Log Firebase operations
   */
  firebase: (operation, collection, docId, error = null) => {
    const level = error ? LOG_LEVELS.ERROR : LOG_LEVELS.DEBUG;
    log(level, `Firebase: ${operation}`, {
      type: 'firebase',
      operation,
      collection,
      docId,
      error: error ? error.message : null
    });
  }
};

/**
 * Error boundary logging helper
 */
export function logErrorBoundary(error, errorInfo) {
  logger.error('React Error Boundary caught an error', {
    type: 'error_boundary',
    error: {
      message: error.message,
      stack: error.stack,
      name: error.name
    },
    errorInfo: {
      componentStack: errorInfo.componentStack
    }
  });
}

/**
 * Unhandled error logging
 */
export function setupGlobalErrorHandling() {
  // Catch unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    logger.error('Unhandled JavaScript error', {
      type: 'unhandled_error',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error ? {
        message: event.error.message,
        stack: event.error.stack
      } : null
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', {
      type: 'unhandled_rejection',
      reason: event.reason,
      promise: event.promise
    });
  });
}

/**
 * Initialize logging system
 */
export function initializeLogging() {
  setupGlobalErrorHandling();
  
  logger.info('Logging system initialized', {
    logLevel: Object.keys(LOG_LEVELS)[CURRENT_LOG_LEVEL],
    consoleEnabled: ENABLE_CONSOLE,
    remoteLoggingEnabled: REMOTE_LOGGING_ENABLED
  });
}

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  initializeLogging();
}

export default logger;
